import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Server, Zap, Shield, HardDrive, Lock, Globe, Cloud, Database, Wrench, ArrowRight } from 'lucide-react';

export const FeaturesPage = () => {
  const featureCategories = [
    {
      title: 'Performance Features',
      description: 'Blazing-fast infrastructure for optimal website speed',
      features: [
        {
          icon: Zap,
          title: 'LiteSpeed Web Servers',
          description: 'Experience up to 40x faster loading times compared to traditional Apache servers. LiteSpeed technology delivers unmatched performance.',
        },
        {
          icon: HardDrive,
          title: 'Pure SSD Storage',
          description: 'All hosting plans come with 100% SSD storage in RAID-10 configuration for maximum speed and redundancy.',
        },
        {
          icon: Globe,
          title: 'Global CDN',
          description: 'Content Delivery Network with 50+ global locations ensures your website loads fast worldwide.',
        },
        {
          icon: Cloud,
          title: 'Advanced Caching',
          description: 'Built-in LiteSpeed Cache, Redis, and Memcached support for dynamic content optimization.',
        },
      ],
    },
    {
      title: 'Security Features',
      description: 'Enterprise-grade security to protect your website',
      features: [
        {
          icon: Shield,
          title: 'Free SSL Certificates',
          description: "Free Let's Encrypt SSL certificates with automatic installation and renewal. Secure your website with HTTPS.",
        },
        {
          icon: Lock,
          title: 'DDoS Protection',
          description: 'Advanced DDoS protection and firewall rules to keep your website safe from attacks.',
        },
        {
          icon: Database,
          title: 'Daily Backups',
          description: 'Automated daily backups with JetBackup solution. One-click restore whenever you need it.',
        },
        {
          icon: Server,
          title: 'Malware Scanning',
          description: 'Regular malware scans and automatic removal to keep your website clean and secure.',
        },
      ],
    },
    {
      title: 'Management Features',
      description: 'Easy-to-use tools for complete control',
      features: [
        {
          icon: Wrench,
          title: 'cPanel Control Panel',
          description: 'Industry-standard cPanel with Softaculous for easy website and email management.',
        },
        {
          icon: Database,
          title: 'Multiple PHP Versions',
          description: 'Support for PHP 5.6 through 8.3. Switch between versions with a single click.',
        },
        {
          icon: Globe,
          title: 'One-Click Installer',
          description: 'Install WordPress, Joomla, Drupal, and 400+ applications with one click.',
        },
        {
          icon: Cloud,
          title: 'Git Integration',
          description: 'Built-in Git version control for developers. Deploy directly from your repositories.',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-foreground relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Everything You Need
            <span className="block mt-2">For Online Success</span>
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Premium features included with every hosting plan. No hidden costs, no compromises.
          </p>
        </div>
      </section>

      {/* Feature Categories */}
      {featureCategories.map((category, catIdx) => (
        <section key={catIdx} className={`py-20 ${catIdx % 2 === 0 ? 'bg-white' : 'bg-secondary/50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold text-foreground mb-3">{category.title}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{category.description}</p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {category.features.map((feature, featureIdx) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={featureIdx}
                    className="card-premium group"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-foreground">
                          {feature.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-20 bg-foreground text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/70 mb-8">
            All these features are included with every hosting plan. Start your journey today!
          </p>
          <Button size="lg" variant="primary-light" asChild data-testid="features-view-plans-btn">
            <a href="/hosting">
              View Hosting Plans
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};
